/**
 * SlashAutocomplete — dropdown anchored above the chat composer that
 * matches when the user types a leading "/". Arrow keys navigate,
 * Enter picks, Esc/blur closes. Picking expands "/<trigger>" into
 * the command's `expansion` (or `<trigger> ` if no expansion).
 */

import type { LucideIcon } from 'lucide-react'
import { Slash } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { cn } from '../lib/cn'
import type { SlashCommand } from '../types'

export interface SlashAutocompleteProps {
  /** The full composer textarea content. The dropdown only renders
   *  when this starts with "/" and has no whitespace yet (still
   *  typing the command name). */
  query: string
  commands: SlashCommand[]
  /** Called with the picked command. Caller is responsible for
   *  rewriting the textarea content (typically replace "/<typed>"
   *  with `expansion` + ` ` if takesArgs, or just `expansion`). */
  onPick: (command: SlashCommand) => void
  /** Pulses the dropdown closed (e.g. user typed a space, ending the
   *  command-name phase). */
  onDismiss: () => void
  /** Imperatively wired to the parent's keyboard handler. Returns
   *  true if the key was handled (parent should preventDefault). */
  registerKeyHandler: (handler: ((e: KeyboardEvent) => boolean) | null) => void
}

export function SlashAutocomplete({ query, commands, onPick, onDismiss, registerKeyHandler }: SlashAutocompleteProps) {
  const [activeIdx, setActiveIdx] = useState(0)
  const ref = useRef<HTMLDivElement>(null)

  // Detect "/<typed>" at the START of the composer text. Only show
  // while the user is still typing the command name (no space yet).
  const slashMatch = useMemo(() => {
    if (!query.startsWith('/')) {
      return null
    }
    const afterSlash = query.slice(1)
    if (afterSlash.includes(' ')) {
      return null
    }
    return afterSlash.toLowerCase()
  }, [query])

  const filtered = useMemo(() => {
    if (slashMatch === null) {
      return []
    }
    if (slashMatch.length === 0) {
      return commands.slice(0, 8)
    }
    return commands
      .filter((c) => c.trigger.toLowerCase().includes(slashMatch) || c.label.toLowerCase().includes(slashMatch))
      .slice(0, 8)
  }, [commands, slashMatch])

  // Reset highlight when filter changes.
  useEffect(() => {
    setActiveIdx(0)
  }, [slashMatch])

  // Wire the parent's keyboard handler. Returns true if we handle the
  // event (so the parent calls preventDefault + stops further handling).
  useEffect(() => {
    if (filtered.length === 0) {
      registerKeyHandler(null)
      return
    }
    const handler = (e: KeyboardEvent): boolean => {
      if (e.key === 'ArrowDown') {
        setActiveIdx((i) => (i + 1) % filtered.length)
        return true
      }
      if (e.key === 'ArrowUp') {
        setActiveIdx((i) => (i - 1 + filtered.length) % filtered.length)
        return true
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        const cmd = filtered[activeIdx]
        if (cmd) {
          onPick(cmd)
          return true
        }
      }
      if (e.key === 'Escape') {
        onDismiss()
        return true
      }
      return false
    }
    registerKeyHandler(handler)
    return () => registerKeyHandler(null)
  }, [filtered, activeIdx, onPick, onDismiss, registerKeyHandler])

  if (filtered.length === 0) {
    return null
  }

  return (
    <div ref={ref} className="as-slash-dropdown" role="listbox" aria-label="Slash commands">
      {filtered.map((c, idx) => {
        const Icon = (c.icon ?? Slash) as LucideIcon
        return (
          <button
            key={c.trigger}
            type="button"
            className={cn('as-slash-item', idx === activeIdx && 'as-slash-item-active')}
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              onPick(c)
            }}
            onMouseEnter={() => setActiveIdx(idx)}
            role="option"
            aria-selected={idx === activeIdx}>
            <Icon className="as-slash-item-icon" />
            <span className="as-slash-item-trigger">/{c.trigger}</span>
            <span className="as-slash-item-label">{c.label}</span>
            {c.takesArgs ? <span className="as-slash-item-args">arg</span> : null}
          </button>
        )
      })}
    </div>
  )
}

/** Helper: rewrite the composer text after a slash command is picked.
 *  - takesArgs: replace "/<typed>" with "<expansion> " (or
 *    "<trigger> ") so the user can keep typing args.
 *  - otherwise: replace with the full expansion (no trailing space). */
export function rewriteAfterSlashPick(currentText: string, picked: SlashCommand): string {
  const expansion = picked.expansion ?? picked.trigger
  if (picked.takesArgs) {
    return `${expansion} `
  }
  return expansion
}
